import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import {
  DashboardOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  LinkOutlined,
  DatabaseOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import TransactionInformatoinTab from '../../../TransactionInformatoinTab';
import ExportHistory from '../../../../../common/TransactionDetalis/ExportHistory';
import ActivityInformationTab from '../../../ActivityInformationTab';
import { resolveActivityDownloadModule } from '../../../../../constants/commonConstants';
import ScoreExplainability from '../../../../../tmCommon/ScoreExplainability';
import AlertHistoryPrevention from '../../../../../tmCommon/components/AlertHistory/AlertHistoryPrevention';
import RelatedCasesPanel from '../RelatedCasesPanel';
import CustomTablesPanel from '../CustomTablesPanel';
import style from './index.scss';

const { TabPane } = Tabs;

// Module 5 — tab contents. Wired tabs reuse the existing Classic components;
// the rest are placeholders until wired. Tab keys are stable (deep links ?tab=).
const TABS = [
  { key: 'explanation', label: 'Explanation', icon: DashboardOutlined },
  {
    key: 'transactions',
    label: 'Transaction Information',
    icon: DollarOutlined
  },
  {
    key: 'activity',
    label: 'Activity Events',
    icon: ThunderboltOutlined
  },
  {
    key: 'history',
    label: 'Alert History',
    icon: HistoryOutlined
  },
  {
    key: 'relatedCases',
    label: 'Related Cases',
    icon: LinkOutlined
  },
  {
    key: 'customTables',
    label: 'Custom Tables',
    icon: DatabaseOutlined
  },
  {
    key: 'downloadHistory',
    label: 'Download History',
    icon: DownloadOutlined
  }
];

// PARKED — NOT deleted. Hiding a key here rather than removing its TABS row keeps
// the whole implementation intact: the row above, the renderBody case, the
// imports, the props and the panel component itself are all untouched.
// TO RESTORE: delete the key from this array. That is the entire change.
//
// 2026-08-19: parked partnerData + relatedCases, on request, "for now".
// 2026-08-23: UN-parked partnerData and parked customTables, so the strip matches
//   the design exactly — Explanation, Transaction Information, Activity Events,
//   Partner Data, Alert History, Download History.
//   Partner Data's BODY is still the placeholder: it has no endpoint or contract
//   (that is the External Partner Data story). The tab is visible and
//   deep-linkable, and must not pretend to have data.
//   Related Cases stays parked — the design does not show it, and it still runs
//   on the mock fixture pending BE-3.
// 2026-08-26: partnerData DELETED, not parked — its TABS entry, its icon import
//   and its placeholder body are all gone, so there is no key left to park. It
//   only ever rendered the placeholder: no endpoint, no contract (the External
//   Partner Data story owns that), so the tab opened onto invented content.
//   Restoring it means re-adding the entry, not deleting a key from this array.
//   The strip is now Explanation, Transaction Information, Activity Events,
//   Alert History, Download History.
//   NOTE: the Partner EVIDENCE card in the Explanation tab is a DIFFERENT
//   surface and is untouched.
const PARKED_TAB_KEYS = ['relatedCases', 'customTables'];

const AlertTabs = ({
  alertId,
  isPrevention,
  module,
  customerId,
  isFromCM,
  alertPartyIdentity,
  relatedCases,
  customTables,
  defaultActiveKey,
  // Overridable ONLY so the parked tabs stay under test — nothing in the app
  // passes it. Without this seam their bodies become unreachable the moment they
  // are parked, stop being exercised, and quietly rot until someone un-parks
  // them and finds them broken. Pass [] to render the full strip.
  parkedTabKeys = PARKED_TAB_KEYS
}) => {
  const visibleTabs = TABS.filter((t) => !parkedTabKeys.includes(t.key));
  // FIN-11649, mirrored from SmartAssessment.js:49-53. A synchronous Activity
  // download must make the Download History tab re-fetch; ExportHistory refetches
  // when refreshKey changes. This was hard-coded to 0 while Activity was our own
  // table and had no download — reusing Classic's tab brings the callback with
  // it, so the state it drives has to exist or the download silently goes
  // unnoticed by the neighbouring tab.
  const [refreshKey, setRefreshKey] = useState(0);
  const onActivityDownloadComplete = () => setRefreshKey((k) => k + 1);

  const validKeys = visibleTabs.map((t) => t.key);
  const [activeKey, setActiveKey] = useState(
    validKeys.includes(defaultActiveKey) ? defaultActiveKey : 'explanation'
  );

  // Reused, self-connected Classic components — same ones the Classic view
  // uses, wired with the same props. Data comes from each component's own
  // Redux/API; tabs render empty until the alert has data in the relevant store.
  const renderBody = (tab) => {
    switch (tab.key) {
      case 'explanation':
        // REUSES CLASSIC WHOLESALE — same component and props SmartAssessment
        // mounts (SmartAssessment.js:149-152).
        //
        // THIS REVERSES AN EARLIER DECISION, deliberately, on the team lead's
        // call: the toggle must switch how the alert is VISUALISED, not change
        // the structure of a shared sub-tab. The custom Spatial panel was
        // reverted to once before for reasons that are now mostly spent — the
        // Score Computation card it cited was removed from BOTH views on
        // 2026-08-22 (FIN-12201), so that objection no longer applies.
        //
        // WHAT THE SWAP COSTS, recorded so nobody rediscovers it: the Spatial
        // table had a Severity column and separate Function / Value columns,
        // and an All Variants + All Group By filter pair. Classic has none of
        // those and adds an Additional Info column instead. Accepted as the
        // price of the two views agreeing.
        return (
          <ScoreExplainability isPrevention={isPrevention} alertId={alertId} />
        );
      case 'transactions':
        return (
          <TransactionInformatoinTab
            alertId={alertId}
            isPrevention={isPrevention}
            isSmartAssessment={isPrevention}
            module={module}
          />
        );
      case 'activity':
        // REUSES CLASSIC WHOLESALE (2026-08-19, user's call) — the same
        // ActivityInformationTab SmartAssessment mounts, with the same props
        // (SmartAssessment.js:137-144). It brings Classic's widget settings, GS
        // query, CSV headers and the FIN-11305 "Triggered Only" switch, none of
        // which the Spatial table had.
        // REPLACED: the custom Spatial ActivityTable, whose 7 design columns the
        // data could not fill — Location and Risk have no source in the StarRocks
        // activity DDL and rendered as permanent em dashes.
        // Same reuse pattern as the Transactions tab directly above.
        return (
          <ActivityInformationTab
            alertId={alertId}
            isPrevention={isPrevention}
            isSmartAssessment={isPrevention}
            onActivityDownloadComplete={onActivityDownloadComplete}
            module={resolveActivityDownloadModule(module)}
          />
        );
      case 'history':
        // REUSES CLASSIC WHOLESALE — same component and props SmartAssessment
        // mounts (SmartAssessment.js:156-159).
        //
        // The Spatial panel it replaces rendered 8 flat columns plus
        // 7/30/90/180/360-day buttons; Classic groups its headers with
        // MultiHeader and carries an `Alert Level/Actions` column with a
        // source-24 branch reading TRIGGERED_RULES_ACTION_SUMMARY — information
        // the Spatial table had nowhere to put. The data source was already
        // Classic's, so only the presentation changes.
        //
        // `alertPartyIdentity` comes from the shell, which already dispatches
        // getPartyIdentity on mount; without it this renders an empty table
        // rather than failing, which is the quiet kind of broken.
        return (
          <AlertHistoryPrevention
            alertPartyIdentity={alertPartyIdentity}
            isFromCM={isFromCM}
          />
        );
      case 'relatedCases':
        // Custom Spatial Related Cases per the Lovable design (summary +
        // primary identifiers + filterable table). On mock; needs BE-3 live.
        return <RelatedCasesPanel relatedCases={relatedCases} />;
      case 'customTables':
        // Config-driven tables from the alert's custom groups/fields. On mock
        // until FIN-11586 supplies the live config.
        return <CustomTablesPanel tables={customTables} />;
      case 'downloadHistory':
        return (
          <ExportHistory
            alertId={alertId}
            isActive={activeKey === 'downloadHistory'}
            module={module}
            refreshKey={refreshKey}
          />
        );
      default:
        // Unreachable today — every tab above has a case. Kept as a safety net
        // so a newly added TABS entry renders something instead of blank.
        return (
          <div className={style.placeholderPanel}>
            <h3>{tab.label}</h3>
          </div>
        );
    }
  };

  return (
    <div className={style.tabsCard}>
      <Tabs activeKey={activeKey} onChange={setActiveKey}>
        {visibleTabs.map((tab) => {
          const { key, label } = tab;
          return (
            /* TEXT ONLY — the design's tab strip carries no icons. The `icon`
               field is kept on the TABS entries: it is still used by the
               placeholder body heading, and dropping it would lose the mapping if
               icons come back. */
            <TabPane key={key} tab={label}>
              {renderBody(tab)}
            </TabPane>
          );
        })}
      </Tabs>
    </div>
  );
};

AlertTabs.propTypes = {
  alertId: PropTypes.string,
  isPrevention: PropTypes.bool,
  module: PropTypes.string,
  customerId: PropTypes.string,
  isFromCM: PropTypes.bool,
  // Handed straight to Classic's AlertHistoryPrevention, which reads `.data`
  // and `.isLoading` off it.
  alertPartyIdentity: PropTypes.object,
  relatedCases: PropTypes.object,
  customTables: PropTypes.array,
  defaultActiveKey: PropTypes.string,
  // Test-only seam; see the destructure above.
  parkedTabKeys: PropTypes.arrayOf(PropTypes.string)
};

AlertTabs.defaultProps = {
  isPrevention: true
};

export default AlertTabs;
